import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { getProductTestingRows } from '@/lib/api';

const OILS_MANUAL = 'https://fssai.gov.in/upload/uploadfiles/files/Manual_Revised_Oil_Fats_22_06_2021.pdf';
const DAIRY_MANUAL = 'https://www.fssai.gov.in/upload/uploadfiles/files/Manual_Dairy_07_10_2022.pdf';
const JAGGERY_STANDARD = 'https://mail.fssai.gov.in/upload/uploadfiles/files/Chapter%202_8%20%28Sweetening%20agents%20including%20Honey%29.pdf';
const TEST_LIBRARY = [
  {
    names: ['castor oil', 'castor seed oil', 'ஆமணக்கு எண்ணெய்'],
    rows: [
      ['Fatty acid profile (GC-FID)', 'Verify ricinoleic acid predominance and detect possible blending with other vegetable oils', 'BIS IS 435; fatty acid profiling'],
      ['Specific gravity', "Detect departures from castor oil's characteristic density", 'BIS IS 435; FSSAI 02.002:2021', OILS_MANUAL],
      ['Refractive index', 'Check oil identity and possible adulteration', 'BIS IS 435; FSSAI oils manual', OILS_MANUAL],
      ['Iodine value', 'Detect abnormal unsaturation, potentially indicating mixing', 'BIS IS 435'],
      ['Saponification value', 'Check consistency with castor oil composition', 'BIS IS 435'],
      ['Acid value / FFA', 'Check deterioration and hydrolysis', 'BIS IS 435; FSSAI oils manual', OILS_MANUAL],
      ['Peroxide value', 'Check oxidative rancidity', 'FSSAI 02.042:2021', OILS_MANUAL],
    ],
  },
  {
    names: ['jaggery', 'jaggery powder', 'cane jaggery', 'cane gur', 'gur', 'vellam', 'வெல்லம்', 'நாட்டு வெல்லம்'],
    rows: [
      ['Moisture', 'Check moisture content and shelf-life stability (cane jaggery: maximum 7%)', 'FSSAI 2.8.4 — Cane Jaggery', JAGGERY_STANDARD],
      ['Sucrose', 'Verify sugar composition (minimum 70% for cane jaggery)', 'FSSAI 2.8.4 — Cane Jaggery', JAGGERY_STANDARD],
      ['Total sugars', 'Verify total sugar content (minimum 90% for cane jaggery)', 'FSSAI 2.8.4 — Cane Jaggery', JAGGERY_STANDARD],
      ['Reducing sugars', 'Check sugar composition against the cane jaggery standard', 'FSSAI 2.8.4 — Cane Jaggery', JAGGERY_STANDARD],
      ['Sulphate ash', 'Assess inorganic residue (maximum 4% for cane jaggery)', 'FSSAI 2.8.4 — Cane Jaggery', JAGGERY_STANDARD],
      ['Acid-insoluble ash', 'Detect sand, soil and other mineral contamination (maximum 0.5%)', 'FSSAI 2.8.4 — Cane Jaggery', JAGGERY_STANDARD],
      ['Extraneous and water-insoluble matter', 'Detect foreign material (maximum 2%)', 'FSSAI 2.8.4 — Cane Jaggery', JAGGERY_STANDARD],
      ['Added synthetic colours', 'Check for prohibited added colour in cane jaggery', 'FSSAI 2.8.4 — Cane Jaggery', JAGGERY_STANDARD],
    ],
  },
  {
    names: ['ghee', 'cow ghee', 'pure cow ghee', 'bilona ghee', 'நெய்'],
    rows: [
      ['Moisture', 'Measure water content and assess storage stability', 'FSSAI 2.1.8; dairy methods manual', DAIRY_MANUAL],
      ['Milk fat content', 'Check conformity with the milk-fat standard', 'FSSAI 2.1.8; dairy methods manual', DAIRY_MANUAL],
      ['Free fatty acids', 'Assess hydrolytic deterioration', 'FSSAI dairy methods manual', DAIRY_MANUAL],
      ['Peroxide value', 'Assess oxidative deterioration', 'FSSAI dairy methods manual', DAIRY_MANUAL],
      ['Vegetable fat adulteration', 'Screen for mixing with non-milk fats', 'FSSAI dairy methods manual', DAIRY_MANUAL],
    ],
  },
  {
    names: ['turmeric', 'turmeric powder', 'மஞ்சள்', 'மஞ்சள் தூள்'],
    rows: [
      ['Lead chromate / added colours', 'Check for potentially hazardous colouring adulterants', 'FSSAI method reference not yet verified'],
      ['Heavy metals', 'Assess contamination with toxic metals', 'FSSAI method reference not yet verified'],
      ['Pesticide residues', 'Assess residues against applicable limits', 'FSSAI method reference not yet verified'],
    ],
  },
  {
    names: ['coconut oil', 'cold pressed coconut oil', 'தேங்காய் எண்ணெய்'],
    rows: [
      ['Fatty acid profile', 'Check oil identity and possible blending', 'FSSAI oils manual', OILS_MANUAL],
      ['Acid value / FFA', 'Assess hydrolytic deterioration', 'FSSAI oils manual', OILS_MANUAL],
      ['Peroxide value', 'Assess oxidation and rancidity', 'FSSAI oils manual', OILS_MANUAL],
      ['Moisture', 'Assess water content and storage stability', 'FSSAI oils manual', OILS_MANUAL],
    ],
  },
];

const normalize = (value) => value.trim().toLocaleLowerCase().replace(/[?.!,]+$/g, '').replace(/\s+/g, ' ');
const localRows = (product) => TEST_LIBRARY.find(entry => entry.names.some(name => normalize(name) === normalize(product)))?.rows;

const parseRows = (answer) => {
  // Accept only strict JSON arrays, never render the model's prose as a result.
  try {
    const data = JSON.parse(answer);
    if (!Array.isArray(data)) return [];
    return data.filter(row => row && typeof row.test === 'string' && typeof row.purpose === 'string' && typeof row.reference === 'string')
      .map(row => [row.test, row.purpose, row.reference, typeof row.source_url === 'string' && /^https:\/\//.test(row.source_url) ? row.source_url : null]);
  } catch {
    return [];
  }
};

export default function TestRequirements() {
  const [question, setQuestion] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    const product = question.trim();
    if (!product || busy) return;
    setQuestion('');
    setBusy(true);
    const id = Date.now();
    setResults(previous => [{ id, product, rows: null, error: false }, ...previous]);
    try {
      const { data } = await getProductTestingRows(product);
      const rows = (data.tests || []).map(item => [item.test, item.purpose, item.reference, item.source_url]);
      setResults(previous => previous.map(result => result.id === id ? { ...result, rows, error: false } : result));
    } catch {
      setResults(previous => previous.map(result => result.id === id ? {
        ...result, rows: [], error: true
      } : result));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (result) => {
    if (!result.rows) return;
    await navigator.clipboard.writeText([
      'Product: ' + result.product,
      'Essential test\\tPurpose\\tReference',
      ...result.rows.map(row => row.slice(0, 3).join('\\t'))
    ].join('\\n'));
  };

  return <div className="space-y-5">
    <h2 className="text-2xl font-semibold">Testing Assistant</h2>
    <Card><CardContent className="p-5 space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input aria-label="Product name" placeholder="Enter product name, e.g. castor oil" value={question} onChange={e => setQuestion(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') ask(); }} />
        <Button onClick={ask} disabled={!question.trim() || busy}>{busy ? 'Searching…' : 'Ask'}</Button>
      </div>
      {results.length > 0 && <div className="max-h-[65vh] overflow-y-auto space-y-6 pr-2" aria-label="Previous testing results">
        {results.map(result => <section key={result.id} className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-lg font-semibold">Product: {result.product}</h3>
            <div className="flex gap-2">
              {result.rows && result.rows.length > 0 && <Button variant="outline" onClick={() => copy(result)}>Copy Table</Button>}
              <Button variant="outline" onClick={() => setResults(previous => previous.filter(item => item.id !== result.id))}>Clear</Button>
            </div>
          </div>
          {result.rows === null ? <p className="text-sm text-muted-foreground">Searching…</p> : result.error ? <p className="text-sm text-destructive">Could not retrieve product-specific tests. Please try again.</p> : result.rows.length === 0 ? <p className="text-sm text-muted-foreground">No testing requirements returned.</p> : <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead><tr className="border-b"><th className="p-3">Essential test</th><th className="p-3">Purpose</th><th className="p-3">Reference</th></tr></thead>
              <tbody>{result.rows.map((row, index) => <tr key={index} className="border-b">
                <td className="p-3 align-top font-semibold">{row[0]}</td>
                <td className="p-3 align-top">{row[1]}</td>
                <td className="p-3 align-top">{row[3] ? <a href={row[3]} target="_blank" rel="noopener noreferrer" className="underline">{row[2]}</a> : row[2]}</td>
              </tr>)}</tbody>
            </table>
          </div>}
        </section>)}
      </div>}
    </CardContent></Card>
  </div>;
}
